import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useRootStore } from '@/stores/rootStore';
import { useStoreOperation } from '@/composables/useStoreOperation';
import { getValidNextPages } from '@/constants/flows';
import type { PageName } from '@/constants/flows';
import type { ComponentPublicInstance, Ref } from 'vue';
import type ButtonGroup from '@/components/ButtonGroup.vue';
import { useConfigStore } from '@/stores/modules/configStore';
import { useFormStore } from '@/stores/modules/formStore';


type ButtonGroupRef = Ref<InstanceType<typeof ButtonGroup> | null>;

/**
 * Composable for handling page navigation with optional button animation
 * @param pageName - Current page identifier in the flow
 * @param errorMessage - Error message to display if navigation fails
 * @returns Navigation utilities including loading state and handler
 */
export function usePageNavigation(pageName: PageName, errorMessage: string) {
    const router = useRouter();
    const store = useRootStore();
    const { handleOperation } = useStoreOperation();
    const isNavigating = ref(false);

    async function retryRouterPush(router: any, route: any, maxAttempts = 3, delayMs = 1000) {
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                await router.push(route);
                return;
            } catch (error: any) {
                if (error.toString().includes('Failed to fetch dynamically imported module')) {
                    if (attempt === maxAttempts) {
                        throw new Error(`Failed to navigate after ${maxAttempts} attempts: ${error.message}`);
                    }
                    console.warn(`Navigation attempt ${attempt} failed, retrying in ${delayMs}ms...`);
                    await new Promise(resolve => setTimeout(resolve, delayMs));
                } else {
                    throw error;
                }
            }
        }
    }

    const handleNavigation = async (_buttonGroupRef?: ButtonGroupRef) => {
        console.log('Navigation started for page:', pageName);
        if (isNavigating.value) {
            console.log('Already navigating, returning');
            return;
        }
        isNavigating.value = true;

        try {
            await handleOperation(
                async () => {
                    // Handle employment-length navigation
                    if (pageName === 'employment-length') {
                        console.log('Handling employment-length navigation');
                        const totalEmploymentHistory = store.form.totalEmploymentHistory;
                        const currentNumber = Number(router.currentRoute.value.params.number);
                      
                        if (totalEmploymentHistory >= 1) {
                            await retryRouterPush(router, { name: 'employment-income', params: { number: 1 } });
                        } else {
                            await retryRouterPush(router, { name: 'employment', params: { number: currentNumber + 1 } });
                        }
                        return;
                    }

                    // Special handling for employment-status navigation
                    if (pageName === 'employment-status') {
                        const currentEmploymentStatus = store.form.employments[
                            Number(router.currentRoute.value.params.number) - 1
                        ]?.statusId;
                        
                        const nonWorkingStatuses = [12, 13, 19, 21, 16, 17];
                        const nextRoute = nonWorkingStatuses.includes(currentEmploymentStatus) 
                            ? 'employment-length' 
                            : 'employment-location';
                            
                        await retryRouterPush(router, { 
                            name: nextRoute, 
                            params: { number: router.currentRoute.value.params.number }
                        });
                        return;
                    }

                    // Handle employment-location navigation
                    if (pageName === 'employment-location') {
                  
                        if (store.progress.formType  === 'dealerGetMyQuote') {
                            await retryRouterPush(router, { 
                                name: 'employment-address-add',
                                params: { number: router.currentRoute.value.params.number }
                            });
                        } else {
                            await retryRouterPush(router, { 
                                name: 'employment-length',
                                params: { number: router.currentRoute.value.params.number }
                            });
                        }
                        return;
                    }

                    if (pageName === 'employment-income') {
                        const totalEmploymentHistory = store.form.totalEmploymentHistory;
                        const currentNumber = Number(router.currentRoute.value.params.number);

                        let totalEmploymentHistoryNumber = 1;
                        if (store.progress.formType  === 'dealerGetMyQuote') totalEmploymentHistoryNumber = 3;
                        if (totalEmploymentHistory >=  totalEmploymentHistoryNumber ) {
                            await retryRouterPush(router, { name: 'amount-to-finance', params: { } });
                        } else {
                            await retryRouterPush(router, { name: 'employment', params: { number: currentNumber + 1 } });
                        }
                        return;
                    }

                    // Handle address-length navigation
                    if (pageName === 'address-length') {
                        const totalAddressHistory = store.form.totalAddressHistory;
                        const currentNumber = Number(router.currentRoute.value.params.number);
                        
                        if (totalAddressHistory >= 3) {
                            await retryRouterPush(router, { name: 'employment', params: { number: 1 } });
                        } else {
                            await retryRouterPush(router, { name: 'address-add', params: { number: currentNumber + 1 } });
                        }
                        
                        return;
                    }

                    console.log('Using default navigation logic');
                    const validNextPages = getValidNextPages(pageName, store.progress.formType);
                    console.log('Valid next pages:', validNextPages);
                    const nextPage = validNextPages[0];
                    
                    if (!nextPage) {
                        throw new Error(`No valid next page found for ${pageName}`);
                    }
                    
                    const currentQuery = router.currentRoute.value.query;
                    const queryParams = Object.keys(currentQuery).length ? { query: currentQuery } : {};
                    
                    // Handle address-specific routing
                    if (nextPage === 'address-add') {
                        await retryRouterPush(router, { name: nextPage, params: { number: '1' }, ...queryParams });
                    } else if (nextPage === 'address-status' || nextPage === 'address-length') {
                        await retryRouterPush(router, { 
                            name: nextPage, 
                            params: { number: router.currentRoute.value.params.number },
                            ...queryParams
                        });
                    } else {
                        await retryRouterPush(router, { name: nextPage, ...queryParams });
                    }
                },
                {
                    errorMessage,
                    throwError: true
                }
            );
        } catch (error) {
            console.error('Navigation error:', error);
            throw error;
        } finally {
            isNavigating.value = false;
            console.log('Navigation completed for page:', pageName);
        }
    };

    return {
        isNavigating,
        handleNavigation
    };
} 